import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/AppShell";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Verify from "@/pages/Verify";
import Settings from "@/pages/Settings";
import Announcements from "@/pages/Announcements";
import Resources from "@/pages/Resources";

import AdminDashboard from "@/pages/admin/Dashboard";
import Students from "@/pages/admin/Students";
import FacultyMgmt from "@/pages/admin/Faculty";
import Batches from "@/pages/admin/Batches";
import Courses from "@/pages/admin/Courses";
import CourseEdit from "@/pages/admin/CourseEdit";
import AssignmentsAdmin from "@/pages/admin/AssignmentsAdmin";
import QuizzesAdmin from "@/pages/admin/QuizzesAdmin";
import CertificatesAdmin from "@/pages/admin/CertificatesAdmin";
import AttendanceReport from "@/pages/admin/AttendanceReport";
import Fees from "@/pages/admin/Fees";
import FeeDetail from "@/pages/admin/FeeDetail";
import Doubts from "@/pages/admin/Doubts";
import DoubtThread from "@/pages/DoubtThread";

import FacultyDashboard from "@/pages/faculty/Dashboard";
import Submissions from "@/pages/faculty/Submissions";
import FacultyAttendance from "@/pages/faculty/Attendance";
import SessionRoster from "@/pages/faculty/SessionRoster";

import StudentDashboard from "@/pages/student/Dashboard";
import StudentCourses from "@/pages/student/Courses";
import LessonPlayer from "@/pages/student/LessonPlayer";
import StudentAssignments from "@/pages/student/Assignments";
import StudentQuizzes from "@/pages/student/Quizzes";
import QuizTake from "@/pages/student/QuizTake";
import QuizResult from "@/pages/student/QuizResult";
import StudentCertificates from "@/pages/student/Certificates";
import StudentAttendance from "@/pages/student/Attendance";
import StudentFees from "@/pages/student/Fees";
import StudentDoubts from "@/pages/student/Doubts";

const HOME = { ADMIN: "/admin/dashboard", FACULTY: "/faculty/dashboard", STUDENT: "/student/dashboard" };

function Protected({ roles, children }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
    if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
    if (roles && !roles.includes(user.role)) return <Navigate to={HOME[user.role] || "/login"} replace />;
    return <AppShell>{children}</AppShell>;
}

function RootRedirect() {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Navigate to="/login" replace />;
    return <Navigate to={HOME[user.role] || "/login"} replace />;
}

export default function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<RootRedirect />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/verify/:certNumber" element={<Verify />} />

                        {/* Admin */}
                        <Route path="/admin/dashboard" element={<Protected roles={["ADMIN"]}><AdminDashboard /></Protected>} />
                        <Route path="/admin/students" element={<Protected roles={["ADMIN", "FACULTY"]}><Students /></Protected>} />
                        <Route path="/admin/faculty" element={<Protected roles={["ADMIN"]}><FacultyMgmt /></Protected>} />
                        <Route path="/admin/batches" element={<Protected roles={["ADMIN"]}><Batches /></Protected>} />
                        <Route path="/admin/courses" element={<Protected roles={["ADMIN", "FACULTY"]}><Courses /></Protected>} />
                        <Route path="/admin/courses/:id" element={<Protected roles={["ADMIN", "FACULTY"]}><CourseEdit /></Protected>} />
                        <Route path="/admin/assignments" element={<Protected roles={["ADMIN", "FACULTY"]}><AssignmentsAdmin /></Protected>} />
                        <Route path="/admin/quizzes" element={<Protected roles={["ADMIN", "FACULTY"]}><QuizzesAdmin /></Protected>} />
                        <Route path="/admin/certificates" element={<Protected roles={["ADMIN", "FACULTY"]}><CertificatesAdmin /></Protected>} />
                        <Route path="/admin/attendance" element={<Protected roles={["ADMIN", "FACULTY"]}><AttendanceReport /></Protected>} />
                        {/* Fees: ADMIN only. FACULTY must have no route, no field, no report — not even a 403 page to stumble into. */}
                        <Route path="/admin/fees" element={<Protected roles={["ADMIN"]}><Fees /></Protected>} />
                        <Route path="/admin/fees/:studentId" element={<Protected roles={["ADMIN"]}><FeeDetail /></Protected>} />
                        {/* Doubts queue: shared by ADMIN and FACULTY — the backend scopes the list to the faculty's own batches. */}
                        <Route path="/admin/doubts" element={<Protected roles={["ADMIN", "FACULTY"]}><Doubts /></Protected>} />
                        <Route path="/admin/doubts/:id" element={<Protected roles={["ADMIN", "FACULTY"]}><DoubtThread /></Protected>} />
                        <Route path="/admin/announcements" element={<Protected roles={["ADMIN"]}><Announcements /></Protected>} />

                        {/* Faculty */}
                        <Route path="/faculty/dashboard" element={<Protected roles={["FACULTY", "ADMIN"]}><FacultyDashboard /></Protected>} />
                        <Route path="/faculty/submissions" element={<Protected roles={["FACULTY", "ADMIN"]}><Submissions /></Protected>} />
                        <Route path="/faculty/attendance" element={<Protected roles={["FACULTY", "ADMIN"]}><FacultyAttendance /></Protected>} />
                        <Route path="/faculty/attendance/:sessionId" element={<Protected roles={["FACULTY", "ADMIN"]}><SessionRoster /></Protected>} />
                        <Route path="/faculty/batches" element={<Protected roles={["FACULTY", "ADMIN"]}><Batches /></Protected>} />
                        <Route path="/faculty/courses" element={<Protected roles={["FACULTY", "ADMIN"]}><Courses /></Protected>} />
                        <Route path="/faculty/announcements" element={<Protected roles={["FACULTY", "ADMIN"]}><Announcements /></Protected>} />

                        {/* Student */}
                        <Route path="/student/dashboard" element={<Protected roles={["STUDENT"]}><StudentDashboard /></Protected>} />
                        <Route path="/student/courses" element={<Protected roles={["STUDENT"]}><StudentCourses /></Protected>} />
                        <Route path="/student/courses/:courseId" element={<Protected roles={["STUDENT"]}><LessonPlayer /></Protected>} />
                        <Route path="/student/assignments" element={<Protected roles={["STUDENT"]}><StudentAssignments /></Protected>} />
                        <Route path="/student/quizzes" element={<Protected roles={["STUDENT"]}><StudentQuizzes /></Protected>} />
                        <Route path="/student/quizzes/:quizId/take" element={<Protected roles={["STUDENT"]}><QuizTake /></Protected>} />
                        <Route path="/student/quizzes/:quizId/result/:attemptId" element={<Protected roles={["STUDENT"]}><QuizResult /></Protected>} />
                        <Route path="/student/certificates" element={<Protected roles={["STUDENT"]}><StudentCertificates /></Protected>} />
                        <Route path="/student/attendance" element={<Protected roles={["STUDENT"]}><StudentAttendance /></Protected>} />
                        <Route path="/student/fees" element={<Protected roles={["STUDENT"]}><StudentFees /></Protected>} />
                        <Route path="/student/doubts" element={<Protected roles={["STUDENT"]}><StudentDoubts /></Protected>} />
                        <Route path="/student/doubts/:id" element={<Protected roles={["STUDENT"]}><DoubtThread /></Protected>} />
                        <Route path="/student/announcements" element={<Protected roles={["STUDENT"]}><Announcements /></Protected>} />

                        <Route path="/resources" element={<Protected><Resources /></Protected>} />

                        <Route path="/settings" element={<Protected><Settings /></Protected>} />
                        <Route path="*" element={<RootRedirect />} />
                    </Routes>
                    <Toaster richColors position="top-right" />
                </BrowserRouter>
            </AuthProvider>
        </ThemeProvider>
    );
}
